/**
 * DecisionFlow — PositionAssignmentLedger
 * ui/src/components/admin/PositionAssignmentLedger.jsx
 * S548 — §6 Assignment History drill-through drawer
 *
 * Opens from History tab row click in CompensationPoliciesWorkspace.
 * Shows a single position's full assignment history:
 *   - Timeline strip at top: fiscal periods as columns, plan bars spanning dates
 *   - Active assignment detail: plan, version, components, effective dates
 *   - Full chronological history: all assignments ordered by effective_date desc
 *
 * Data source: GET /api/v1/plan-assignments?position_id={id}
 * No missing routes — uses confirmed live endpoint.
 *
 * Controls:
 *   - No P4-xx codes on any user-facing surface
 *   - Human-readable codes only — never UUIDs or integer IDs in display
 *   - Modal does not close on outside click
 *   - CSS tokens only: rgb(var(--color-*))
 *   - No AI in any path (NN-03)
 */

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ChevronDown, ChevronUp, CheckCircle2,
  Clock, AlertTriangle, GitBranch, Layers3,
  Calendar, User, Building2, Globe, ArrowRight,
  RefreshCw, Info, XCircle, ChevronRight,
} from "lucide-react";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Status color mapping — assignment_status CHECK: active, superseded, cancelled
// ---------------------------------------------------------------------------
const STATUS_CFG = {
  active:     { label: "Active",     cls: "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]" },
  superseded: { label: "Superseded", cls: "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]" },
  cancelled:  { label: "Cancelled",  cls: "border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]" },
};

// Timeline bar colors — rotate across plans for visual distinction
const BAR_COLORS = [
  "bg-[rgb(var(--color-primary))]/80",
  "bg-[rgb(var(--color-success))]/70",
  "bg-amber-400/80",
  "bg-purple-400/80",
  "bg-pink-400/80",
  "bg-teal-400/80",
];

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
function StatusPill({ status }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.active;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-tok-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function LoadingRow() {
  return (
    <div className="animate-pulse space-y-3 px-6 py-4">
      <div className="h-4 w-2/3 rounded skeleton" />
      <div className="h-4 w-1/2 rounded skeleton" />
      <div className="h-4 w-3/4 rounded skeleton" />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="mx-6 my-3 rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load assignment history."}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimelineStrip — horizontal period overview
// Rows: one per unique plan_code. Columns: fiscal periods ordered by date.
// Bars: colored spans for each assignment's effective_date → end_date range.
// ---------------------------------------------------------------------------
function TimelineStrip({ assignments, onSelectAssignment, selectedId }) {
  // Collect unique periods in chronological order
  const periods = useMemo(() => {
    const map = {};
    assignments.forEach((a) => {
      const key = a.fiscal_period_code ?? a.fiscal_period ?? String(a.fiscal_period_id);
      if (!map[key]) map[key] = { key, label: a.fiscal_period_label ?? a.fiscal_period_code ?? a.fiscal_period ?? key };
    });
    return Object.values(map);
  }, [assignments]);

  // Collect unique plan codes
  const planCodes = useMemo(() => {
    const seen = new Set();
    const list = [];
    assignments.forEach((a) => {
      const code = a.plan_code ?? a.plan_name ?? `Plan ${a.plan_id}`;
      if (!seen.has(code)) { seen.add(code); list.push(code); }
    });
    return list;
  }, [assignments]);

  // Color map: plan_code → bar color class
  const colorMap = useMemo(() => {
    const m = {};
    planCodes.forEach((code, i) => { m[code] = BAR_COLORS[i % BAR_COLORS.length]; });
    return m;
  }, [planCodes]);

  if (periods.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: Math.max(400, periods.length * 100) }}>
        {/* Period headers */}
        <div className="flex border-b border-[rgb(var(--color-border))] pb-1">
          <div className="w-36 shrink-0 text-tok-xs text-[rgb(var(--color-text-muted))]" />
          {periods.map((p) => (
            <div key={p.key} className="flex-1 text-center text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              {p.label}
            </div>
          ))}
        </div>

        {/* One row per plan code */}
        {planCodes.map((code) => {
          const planAssignments = assignments.filter(
            (a) => (a.plan_code ?? a.plan_name ?? `Plan ${a.plan_id}`) === code
          );
          return (
            <div key={code} className="flex items-center py-1.5">
              <div className="w-36 shrink-0 truncate pr-2 text-tok-xs font-medium text-[rgb(var(--color-text))]">
                {code}
              </div>
              {periods.map((p) => {
                const match = planAssignments.find(
                  (a) => (a.fiscal_period_code ?? a.fiscal_period ?? String(a.fiscal_period_id)) === p.key
                );
                return (
                  <div key={p.key} className="flex-1 px-0.5">
                    {match ? (
                      <button
                        onClick={() => onSelectAssignment(match)}
                        title={`${code} · ${match.assignment_status} · ${match.effective_date}`}
                        className={`w-full rounded px-1 py-1.5 text-tok-xs font-medium text-white transition hover:opacity-90 ${
                          colorMap[code]
                        } ${selectedId === match.plan_assignment_id ? "ring-2 ring-white ring-offset-1" : ""}`}
                      >
                        <span className="truncate block">{match.assignment_status === "superseded" ? "↩" : "✓"}</span>
                      </button>
                    ) : (
                      <div className="w-full rounded border border-dashed border-[rgb(var(--color-border))] py-1.5" />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Color legend */}
        <div className="mt-2 flex flex-wrap gap-3 border-t border-[rgb(var(--color-border))] pt-2">
          {planCodes.map((code) => (
            <div key={code} className="flex items-center gap-1.5">
              <div className={`h-2.5 w-2.5 rounded-sm ${colorMap[code]}`} />
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{code}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="h-2.5 w-2.5 rounded-sm border border-dashed border-[rgb(var(--color-border))]" />
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">No assignment</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AssignmentDetail — single assignment expanded view
// Shows plan, version, component, dates, status, supersession chain
// ---------------------------------------------------------------------------
function AssignmentDetail({ assignment }) {
  const [expanded, setExpanded] = useState(false);

  if (!assignment) return null;

  const planCode    = assignment.plan_code    ?? `Plan ${assignment.plan_id ?? "—"}`;
  const planName    = assignment.plan_name    ?? planCode;
  const version     = assignment.plan_version_resolved ?? assignment.version ?? null;
  const component   = assignment.component_label ?? assignment.component ?? assignment.plan_component_id ?? "—";
  const bu          = assignment.business_unit_code ?? assignment.business_unit ?? assignment.business_unit_id ?? "—";
  const period      = assignment.fiscal_period_label ?? assignment.fiscal_period_code ?? assignment.fiscal_period ?? "—";
  const effDate     = assignment.effective_date ?? "—";
  const endDate     = assignment.end_date ?? "Open";
  const status      = assignment.assignment_status ?? "active";
  const createdAt   = assignment.created_at ? new Date(assignment.created_at).toLocaleDateString() : "—";
  const supersededBy = assignment.superseded_by ?? null;

  return (
    <div className={`rounded-xl border p-4 transition ${
      status === "active"
        ? "border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5"
        : status === "cancelled"
          ? "border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5"
          : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
    }`}>
      {/* Header */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <StatusPill status={status} />
            {version && (
              <span className="flex items-center gap-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                <GitBranch className="h-3 w-3" />v{version}
              </span>
            )}
          </div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{planName}</p>
          <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">{planCode}</p>
        </div>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-1 text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {expanded ? "Less" : "More"}
        </button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-tok-xs">
        <div>
          <span className="text-[rgb(var(--color-text-muted))]">Period </span>
          <span className="font-medium text-[rgb(var(--color-text))]">{period}</span>
        </div>
        <div>
          <span className="text-[rgb(var(--color-text-muted))]">Business Unit </span>
          <span className="font-medium text-[rgb(var(--color-text))]">{bu}</span>
        </div>
        <div>
          <span className="text-[rgb(var(--color-text-muted))]">Effective </span>
          <span className="font-medium text-[rgb(var(--color-text))]">{effDate}</span>
        </div>
        <div>
          <span className="text-[rgb(var(--color-text-muted))]">End </span>
          <span className="font-medium text-[rgb(var(--color-text))]">{endDate}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="mt-3 space-y-2 border-t border-[rgb(var(--color-border))]/40 pt-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-tok-xs">
            <div>
              <span className="text-[rgb(var(--color-text-muted))]">Component </span>
              <span className="font-medium text-[rgb(var(--color-text))]">{component}</span>
            </div>
            <div>
              <span className="text-[rgb(var(--color-text-muted))]">Created </span>
              <span className="font-medium text-[rgb(var(--color-text))]">{createdAt}</span>
            </div>
            {supersededBy && (
              <div className="col-span-2">
                <span className="text-[rgb(var(--color-text-muted))]">Superseded by assignment </span>
                <span className="font-mono font-medium text-[rgb(var(--color-text))]">{supersededBy}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PositionAssignmentLedger — main drawer
// ---------------------------------------------------------------------------
export default function PositionAssignmentLedger({ position, onClose }) {
  const [assignments, setAssignments] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [selected,    setSelected]    = useState(null); // assignment clicked in timeline

  useEffect(() => {
    if (!position?.position_id) return;
    setLoading(true);
    setError(null);
    setAssignments([]);
    setSelected(null);

    fetch(`${API_BASE}/plan-assignments?position_id=${position.position_id}`, {
      credentials: "include",
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        const raw = data?.plan_assignments ?? data?.assignments ?? data?.data ?? [];
        // Sort: active first, then by effective_date desc
        const sorted = [...raw].sort((a, b) => {
          if (a.assignment_status === "active" && b.assignment_status !== "active") return -1;
          if (b.assignment_status === "active" && a.assignment_status !== "active") return 1;
          return new Date(b.effective_date) - new Date(a.effective_date);
        });
        setAssignments(sorted);
        // Auto-select most recent active
        setSelected(sorted.find((a) => a.assignment_status === "active") ?? sorted[0] ?? null);
      })
      .catch((e) => setError(e.message ?? "Unable to load assignment history."))
      .finally(() => setLoading(false));
  }, [position?.position_id]);

  const activeAssignments     = assignments.filter((a) => a.assignment_status === "active");
  const historicalAssignments = assignments.filter((a) => a.assignment_status !== "active");

  const positionName = position?.full_name ?? position?.employee_id ?? position?.position_id ?? "Position";
  const roleLabel    = position?.role_name ?? position?.role_code ?? null;
  const buLabel      = position?.business_unit_code ?? position?.business_unit ?? null;
  const hireDate     = position?.hire_date ?? null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-end">
        {/* Backdrop — does not close on click per modal spec */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black"
        />

        {/* Drawer */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.22 }}
          className="relative z-10 flex h-full w-full max-w-2xl flex-col border-l border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg))] shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-6 py-4">
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <User className="h-4 w-4 text-[rgb(var(--color-primary))]" />
                <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">{positionName}</h2>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                {roleLabel && <span>{roleLabel}</span>}
                {buLabel   && <><span>·</span><span>{buLabel}</span></>}
                {hireDate  && <><span>·</span><span>Hired {hireDate}</span></>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">

            {loading && <LoadingRow />}
            {error   && <ErrorNote message={error} />}

            {!loading && !error && assignments.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Calendar className="mb-3 h-8 w-8 text-[rgb(var(--color-text-muted))]" />
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No assignment history</p>
                <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  This position has no plan assignments on record.
                </p>
              </div>
            )}

            {!loading && !error && assignments.length > 0 && (
              <div className="space-y-0">

                {/* ── Section 1: Timeline strip ── */}
                <div className="border-b border-[rgb(var(--color-border))] px-6 py-4">
                  <p className="mb-3 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                    Assignment Timeline
                  </p>
                  <TimelineStrip
                    assignments={assignments}
                    onSelectAssignment={setSelected}
                    selectedId={selected?.plan_assignment_id}
                  />
                </div>

                {/* ── Section 2: Selected assignment detail ── */}
                {selected && (
                  <div className="border-b border-[rgb(var(--color-border))] px-6 py-4">
                    <p className="mb-3 text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                      Selected Assignment
                    </p>
                    <AssignmentDetail assignment={selected} />
                  </div>
                )}

                {/* ── Section 3: Active assignments ── */}
                {activeAssignments.length > 0 && (
                  <div className="border-b border-[rgb(var(--color-border))] px-6 py-4">
                    <div className="mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))]" />
                      <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                        Active ({activeAssignments.length})
                      </p>
                    </div>
                    <div className="space-y-2">
                      {activeAssignments.map((a) => (
                        <button
                          key={a.plan_assignment_id}
                          onClick={() => setSelected(a)}
                          className={`w-full rounded-xl border px-4 py-3 text-left transition hover:border-[rgb(var(--color-primary))]/40 ${
                            selected?.plan_assignment_id === a.plan_assignment_id
                              ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                              : "border-[rgb(var(--color-success))]/20 bg-[rgb(var(--color-success))]/5"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                                {a.plan_code ?? a.plan_name ?? `Plan ${a.plan_id}`}
                              </p>
                              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                                {a.fiscal_period_label ?? a.fiscal_period_code ?? a.fiscal_period ?? "—"}
                                {" · "}
                                {a.effective_date ?? "—"} → {a.end_date ?? "Open"}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Section 4: Full history ── */}
                {historicalAssignments.length > 0 && (
                  <div className="px-6 py-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
                      <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                        History ({historicalAssignments.length})
                      </p>
                    </div>
                    <div className="space-y-2">
                      {historicalAssignments.map((a) => (
                        <button
                          key={a.plan_assignment_id}
                          onClick={() => setSelected(a)}
                          className={`w-full rounded-xl border px-4 py-3 text-left transition hover:border-[rgb(var(--color-primary))]/30 ${
                            selected?.plan_assignment_id === a.plan_assignment_id
                              ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                              : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                                <StatusPill status={a.assignment_status} />
                                {a.plan_version_resolved && (
                                  <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                                    v{a.plan_version_resolved}
                                  </span>
                                )}
                              </div>
                              <p className="truncate text-tok-sm font-medium text-[rgb(var(--color-text))]">
                                {a.plan_code ?? a.plan_name ?? `Plan ${a.plan_id}`}
                              </p>
                              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                                {a.fiscal_period_label ?? a.fiscal_period_code ?? a.fiscal_period ?? "—"}
                                {" · "}
                                {a.effective_date ?? "—"} → {a.end_date ?? "Open"}
                                {a.superseded_by && " · Superseded"}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 shrink-0 text-[rgb(var(--color-text-muted))]" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] px-6 py-4">
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              {assignments.length} assignment record{assignments.length !== 1 ? "s" : ""}
              {activeAssignments.length > 0 && ` · ${activeAssignments.length} active`}
            </p>
            <button
              onClick={onClose}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
